/**
 * Magic Shimmer — replaces the heavy full-screen confetti burst with a tasteful
 * "AI ready" celebration:
 *
 *   1. A soft radial aurora glow that pulses once from the page center
 *   2. A handful of upward-drifting sparkles (Sparkles icon, NOT confetti rectangles)
 *   3. Auto-dismisses cleanly with no leftover DOM nodes
 *
 * Density and motion are deliberately understated so they:
 *   - work as well on mobile (6.7") as desktop (24")
 *   - never obscure the content the user came to see
 *   - feel premium, like Apple/Linear/Vercel "completion" feedback
 *
 * API is identical to the old <Confetti/> for drop-in compatibility.
 */
import React, { useEffect, useState, useRef } from 'react';
import { Sparkles } from 'lucide-react';

const SPARKLE_COUNT = 5;              // tasteful — was 14, then 200 (original confetti)
const SPARKLE_DURATION_MS = 1200;     // each sparkle's lifetime
const TOTAL_DURATION_MS = 1400;       // overall effect (slightly > sparkle dur)

const randomBetween = (min, max) => Math.random() * (max - min) + min;

/**
 * Drop-in replacement for the old <Confetti/> component.
 * Keep the prop names identical so callers don't change.
 */
export function Confetti({ active = false, duration = TOTAL_DURATION_MS, onComplete }) {
  const [visible, setVisible] = useState(false);
  const [sparkles, setSparkles] = useState([]);
  const keyRef = useRef(0);

  useEffect(() => {
    if (!active) return;
    keyRef.current += 1;
    const myKey = keyRef.current;

    // Generate a tight, centered cloud of sparkles around the AI Brain logo
    // (top-center of dashboard) — feels like a focused "Brain awakened" moment
    // instead of a viewport-wide starfield.
    const items = [];
    for (let i = 0; i < SPARKLE_COUNT; i++) {
      items.push({
        id: `${myKey}-${i}`,
        left: randomBetween(40, 60),                   // % across viewport (centered)
        bottom: randomBetween(60, 78),                 // top half (around hero/brain logo)
        size: randomBetween(12, 18),                   // px (smaller, more refined)
        delay: randomBetween(0, 350),                  // ms — quick stagger
        drift: randomBetween(-20, 20),                 // px horizontal drift
        rise: randomBetween(40, 90),                   // px upward travel
        duration: randomBetween(900, 1200),            // ms
        hue: randomBetween(200, 280),                  // blue → violet only (brand)
      });
    }
    setSparkles(items);
    setVisible(true);

    const t = setTimeout(() => {
      if (keyRef.current !== myKey) return;
      setVisible(false);
      setSparkles([]);
      onComplete?.();
    }, duration);
    return () => clearTimeout(t);
  }, [active, duration, onComplete]);

  if (!visible) return null;

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-[60] overflow-hidden"
      data-testid="magic-shimmer"
    >
      {/* Drifting sparkles only — aurora pulse removed for a cleaner, more refined feel */}
      {sparkles.map((s) => (
        <span
          key={s.id}
          className="absolute magic-sparkle"
          style={{
            left: `${s.left}%`,
            bottom: `${s.bottom}%`,
            color: `hsl(${s.hue}, 90%, 65%)`,
            filter: `drop-shadow(0 0 6px hsl(${s.hue}, 90%, 60%))`,
            animationDelay: `${s.delay}ms`,
            animationDuration: `${s.duration}ms`,
            ['--rise']: `-${s.rise}px`,
            ['--drift']: `${s.drift}px`,
          }}
        >
          <Sparkles size={s.size} strokeWidth={1.6} />
        </span>
      ))}

      <style>{`
        @keyframes magicAurora {
          0%   { opacity: 0; transform: scale(0.4); }
          30%  { opacity: 0.55; }
          100% { opacity: 0;    transform: scale(2.4); }
        }
        @keyframes magicSparkle {
          0%   { opacity: 0; transform: translate(0,0) scale(0.4) rotate(0deg); }
          15%  { opacity: 1; }
          70%  { opacity: 1; }
          100% { opacity: 0; transform: translate(var(--drift), var(--rise)) scale(0.95) rotate(35deg); }
        }
        .magic-aurora {
          width: 720px;
          height: 720px;
          max-width: 90vw;
          max-height: 90vh;
          border-radius: 50%;
          background: radial-gradient(circle,
            rgba(99,102,241,0.32) 0%,
            rgba(139,92,246,0.20) 35%,
            rgba(59,130,246,0.10) 60%,
            transparent 75%);
          animation: magicAurora 1800ms cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
        }
        .magic-sparkle {
          animation-name: magicSparkle;
          animation-timing-function: cubic-bezier(0.2, 0.7, 0.2, 1);
          animation-fill-mode: forwards;
          will-change: transform, opacity;
        }
      `}</style>
    </div>
  );
}

export default Confetti;
